import moment from "moment";
import { useMemo, type ReactNode } from "react";
import EventDisplay from "./EventDisplay";
import DroppableCell from "./DroppableCell";
import { useGridDates } from "../../../../hooks/grid/useGridDates";
import { useDragPassThrough } from "../../../../hooks/grid/useDragPassThrough";
import { hasGroupDivider } from "../../../../helpers/grid/getGroupRowStyle";
import { BASE_ROW_HEIGHT, EVENT_ROW_HEIGHT, GROUP_BY_HEIGHT, LANE_PADDING } from "../../../../constants/height";
import type { Event } from "../../../../types/Event";
import type { Resource } from "../../../../types/Resource";

interface DayViewProps {
    resources: Resource[];
    events: Event[];
    resourceLayouts: Record<string, { totalLanes: number }>;
    eventLayouts: Record<string, { laneIndex: number }>;
    cellWidth: number;
    unassignedShifts?: boolean;
}

function DayView({
    resources,
    events,
    resourceLayouts,
    eventLayouts,
    cellWidth,
    // unassignedShifts
}: DayViewProps) {
    const { gridDates, gridStartTime, gridEndTime } = useGridDates();
    const isDragPassThrough = useDragPassThrough();

    const cumulativeRowTops = useMemo(() => {
        let cumulativeHeight = 0;
        return resources.reduce<Record<string, number>>((acc, resource) => {
            acc[resource.id] = cumulativeHeight;
            const layout = resourceLayouts[resource.id];
            const rowHeight = layout ? layout.totalLanes * BASE_ROW_HEIGHT : BASE_ROW_HEIGHT;
            cumulativeHeight += rowHeight;
            return acc;
        }, {});
    }, [resources, resourceLayouts]);

    if (!gridDates?.length || cellWidth <= 0) return null;

    const gridElements: ReactNode[] = [];

    // 1. Render background cells
    resources.forEach((resource, resourceIndex) => {
        gridDates.forEach((cellTime, colIndex) => {
            const cellStart = moment(cellTime);
            const cellEnd = cellStart.clone().add(1, "hour");

            gridElements.push(
                <DroppableCell
                    key={`cell-${resource.id}-${cellTime}`}
                    resource={resource}
                    cellTime={cellTime}
                    gridColumnStart={colIndex + 1}
                    gridRowStart={resourceIndex + 1}
                    cellStart={cellStart}
                    cellEnd={cellEnd}
                    cellWidth={cellWidth}
                    withGroupDivider={hasGroupDivider(resources, resourceIndex)}
                />,
            );
        });
    });

    // 2. Render events
    events.forEach((event: any) => {
        let eventLayout: any = eventLayouts[event.id];
        if (!eventLayout && event?.type === "open-shifts") {
            eventLayout = { laneIndex: 0 };
        } else {
            if (!eventLayout) return;
        }

        const resourceIndex = resources.findIndex((r) => r.id === event.resourceId);

        if (resourceIndex === -1) return;
        const viewByCountBefore = resources
            .slice(0, resourceIndex)
            .filter(r => ((resourceLayouts[r.id] as any)?.type === "view-by"))
            .length;

        const viewByOffsetPx = viewByCountBefore * (GROUP_BY_HEIGHT - 1);
        const eventStart = moment(event.startDate);
        const eventEnd = moment(event.endDate);

        if (eventEnd.isBefore(moment(gridStartTime)) || eventStart.isAfter(moment(gridEndTime))) return;

        const effectiveEventStart = moment.max(eventStart, moment(gridStartTime));
        const effectiveEventEnd = moment.min(eventEnd, moment(gridEndTime));

        const offsetMinutes = moment.duration(effectiveEventStart.diff(moment(gridStartTime))).asMinutes();
        const durationMinutes = moment.duration(effectiveEventEnd.diff(effectiveEventStart)).asMinutes();

        const leftPx = (offsetMinutes / 60) * cellWidth;
        const widthPx = (durationMinutes / 60) * cellWidth;

        if (widthPx < 0) return;

        const rowTop = cumulativeRowTops[event.resourceId];
        const resource = resources.find((r) => r.id === event.resourceId);

        // Unavailable events overlay the whole row on top of the shifts instead of taking a lane
        if (event?.type === "unavailable") {
            const totalLanes = resourceLayouts[event.resourceId]?.totalLanes ?? 1;
            gridElements.push(
                <EventDisplay
                    key={event.id}
                    event={{ ...event, resource }}
                    cellWidth={cellWidth}
                    style={{
                        position: "absolute",
                        top: `${rowTop - viewByOffsetPx + viewByCountBefore}px`,
                        left: `${leftPx}px`,
                        width: `${widthPx}px`,
                        height: `${totalLanes * BASE_ROW_HEIGHT}px`,
                        zIndex: 15,
                        opacity: 0.7,
                        pointerEvents: "none",
                    }}
                />
            );
            return;
        }

        const laneIndex = eventLayout.laneIndex;
        const topPx = rowTop + laneIndex * (EVENT_ROW_HEIGHT + LANE_PADDING) + LANE_PADDING;

        gridElements.push(
            <EventDisplay
                key={event.id}
                event={{ ...event, resource }}
                cellWidth={cellWidth}
                style={{
                    position: "absolute",
                    top: `${topPx - viewByOffsetPx + viewByCountBefore}px`,
                    left: `${leftPx}px`,
                    width: `${widthPx}px`,
                    height: `${EVENT_ROW_HEIGHT}px`,
                    pointerEvents: isDragPassThrough(event.id) ? "none" : undefined,
                }}
            />
        );
    });

    return <>{gridElements}</>;
}

export default DayView;
